import { useState, useEffect, useRef } from 'react'

interface UseTypewriterOptions {
  text: string
  isActive: boolean
  charsPerFrame?: number
}

export const useTypewriter = ({
  text,
  isActive,
  charsPerFrame = 3,
}: UseTypewriterOptions): string => {
  const [displayedText, setDisplayedText] = useState(text)
  const targetRef = useRef(text)
  const displayedRef = useRef(text)
  const rafRef = useRef<number | null>(null)

  targetRef.current = text

  useEffect(() => {
    if (!isActive) {
      setDisplayedText(text)
      displayedRef.current = text
      return
    }

    const tick = () => {
      const target = targetRef.current
      const current = displayedRef.current

      if (current.length < target.length) {
        const next = target.slice(0, current.length + charsPerFrame)
        displayedRef.current = next
        setDisplayedText(next)
        rafRef.current = requestAnimationFrame(tick)
      } else if (current !== target) {
        displayedRef.current = target
        setDisplayedText(target)
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [text, isActive, charsPerFrame])

  return displayedText
}
